import React, { useCallback, useMemo, useState, useRef } from "react";
import { RootState } from "src/store";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Popover } from "src/assets/imports/antdComponents";
import { ICitationReference, ICitationTagProps, ReferenceDrawerVisibilityType, StudyType } from "../../models/interfaces";
import {
	clearReferenceSelectedItemTrigger,
	resetActiveWidgetData,
	setActiveCitationReferences,
	setAskKuralReferenceActiveTab,
	setReferenceDrawerVisibility,
	setAutoOpenTranscriptItem,
} from "../../states/askKuralSlice";
import { setCountSelectedQuote, setCountWidgetOpen } from "../../states/countWidgetSlice";
import TranscriptPreviewPopover from "../TranscriptPreviewPopover";
import "./style.css";
import { useActionTracking } from "src/components/UserMonitoring/useActionTracking";
import { ActionTypes, ModuleTypes } from "src/pages/Studies/models/interfaces";
import TagLoader from "public/icon/TagLoader.svg";
import ReportsPreviewPopover from "../ReportsPreviewPopover";
import ReportIcon from "public/icon/report_icon.svg";
import { useCitationLoaderState } from "../../hooks/useCitationLoaderState";

const toCitationStudyType = (rawStudyType?: string): string => {
	const baseStudyType = (rawStudyType || "").replace(/_\d+$/, "");
	return baseStudyType === "qual" ? StudyType.Transcripts : baseStudyType;
};

const InlineReferenceTag: React.FC<ICitationTagProps> = ({ citationId, studyType, messageId, references, isReferenceLoading = false, isReferenceTerminal = false, isProcessComplete, expectedReferenceCount, onCitationClick }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { activeCitationReferences } = useSelector((state: RootState) => state.askKuralSlice);
	const [popoverOpen, setPopoverOpen] = useState(false);

	const scrollUniqueIdRef = useRef(`${citationId}-${crypto.randomUUID()}`);
	const scrollUniqueId = scrollUniqueIdRef.current;

	const { trackAction } = useActionTracking(ModuleTypes.AskKural);

	// Add this useEffect after your other hooks, before the return statement
	React.useEffect(() => {
		// Preload all preview images from references
		if (Array.isArray(references) && references?.length > 0) {
			references.forEach((ref) => {
				if (ref.context_preview_image?.image_path) {
					const img = new Image();
					img.src = ref.context_preview_image.image_path;
				}
			});
		}
	}, [references]);

	// Calculate display info from references prop. ``citationType`` is the
	// authoritative value for picking the per-source icon — the upstream
	// citation enricher stamps it on every resolved ref. The legacy fallback
	// to the ``studyType`` prop only fires when references haven't loaded yet
	// (loader/empty states).
	const displayInfo = useMemo(() => {
		// Sub-agent tabs pass dispatchKey forms like "transcripts_2" / "reports_2"
		// when the harness called the same sub-agent more than once in a turn —
		// strip the invocation suffix so the citationType matches StudyType.
		const baseStudyType = toCitationStudyType(studyType);

		if (!Array.isArray(references) || references.length === 0) {
			return {
				type: "Quotes",
				citationType: baseStudyType,
				count: 0,
				hasData: false,
			};
		}

		const firstRef = references[0];
		const citationType: string = firstRef.citation_type || baseStudyType;
		let type = "Quotes";
		if (citationType === StudyType.Reports) type = "Reports";
		else if (citationType === StudyType.Quant) type = "Quant";
		else if (citationType === StudyType.Notes) type = "Notes";
		else type = "Quotes"; // transcripts / qual / unknown

		return {
			type,
			citationType,
			count: references.length,
			hasData: true,
		};
	}, [references, studyType]);

	// Narrow ``displayInfo.citationType`` (which may be any string) to a
	// valid StudyType for the reference-drawer tab dispatch. The drawer
	// itself supports Reports / Transcripts / Notes / Quant; any other value
	// (legacy or empty) falls back to Transcripts to preserve previous
	// behaviour for unclassified citations.
	const referenceDrawerTab = useMemo<StudyType>(() => {
		switch (displayInfo.citationType) {
			case StudyType.Reports:
				return StudyType.Reports;
			case StudyType.Notes:
				return StudyType.Notes;
			case StudyType.Quant:
				return StudyType.Quant;
			default:
				return StudyType.Transcripts;
		}
	}, [displayInfo.citationType]);

	// Per-source icon picker. Two of the four study types ship as bespoke
	// SVG components (kept for visual parity with v3.3); the remaining two
	// use the same material-symbols glyphs as the harness tab strip in
	// CombinedMessageDisplay so the citation pill mirrors its tab.
	const renderTypeIcon = useCallback(() => {
		switch (displayInfo.citationType) {
			case StudyType.Reports:
				return <ReportIcon />;
			case StudyType.Quant:
				return <i className="material-symbols-outlined text-base-16">bar_chart</i>;
			case StudyType.Notes:
				return <i className="material-symbols-outlined text-base-16">sticky_note_2</i>;
			case StudyType.Transcripts:
			default:
				return <i className="material-symbols-outlined filled-icon text-base">format_quote</i>;
		}
	}, [displayInfo.citationType]);

	// Determine if we should show loading state (shared across all inline citation tags).
	const shouldShowLoader = useCitationLoaderState({
		hasData: displayInfo.hasData,
		isReferenceLoading,
		isReferenceTerminal,
		hasAllExpectedReferences: !expectedReferenceCount || displayInfo.count >= expectedReferenceCount,
		isProcessComplete,
	});

	const isSelected = useMemo(() => activeCitationReferences?.citationId === citationId, [activeCitationReferences, citationId]);

	// Handle click
	const handleClick = useCallback(() => {
		if (!displayInfo.hasData) {
			console.warn("No references available for citation:", citationId);
			return;
		}

		if (activeCitationReferences?.citationId === citationId) {
			return;
		}

		dispatch(
			setActiveCitationReferences({
				citationId,
				// Store the resolved citation_type (one of the four StudyType
				// source values), not the raw studyType prop — that prop can
				// be a dispatchKey like "reports_2" for multi-invocation tabs,
				// which the drawer's tab logic doesn't recognize.
				studyType: referenceDrawerTab,
				messageId,
				references,
			}),
		);
		dispatch(clearReferenceSelectedItemTrigger());
		dispatch(resetActiveWidgetData());
		dispatch(setCountSelectedQuote(null));
		dispatch(setCountWidgetOpen(false));
		trackAction(ActionTypes.REFLECTIVE_QUOTES_DRAWER_VIEWED, { feature: true, type: undefined });
		onCitationClick?.(citationId, studyType, references, messageId, scrollUniqueId);
	}, [citationId, studyType, references, messageId, displayInfo.hasData, onCitationClick, activeCitationReferences, referenceDrawerTab, dispatch, scrollUniqueId, trackAction]);

	// Handle keyboard
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleClick();
			}
		},
		[handleClick],
	);

	const handleViewDetailsFromPopover = useCallback(
		(selectedRef: ICitationReference) => {
			setPopoverOpen(false);

			if (!displayInfo.hasData) {
				console.warn("No references available for citation:", citationId);
				return;
			}

			// Set the full citation group in Redux. Use the resolved
			// citation_type so the drawer's tab + content logic sees a clean
			// StudyType (rather than a dispatchKey like "reports_2").
			dispatch(
				setActiveCitationReferences({
					citationId,
					studyType: referenceDrawerTab,
					messageId,
					references,
				}),
			);

			// Reset widget states
			dispatch(clearReferenceSelectedItemTrigger());
			dispatch(resetActiveWidgetData());
			dispatch(setCountWidgetOpen(false));
			dispatch(setCountSelectedQuote(null));

			// Drive the drawer's active tab off the resolved citation_type so
			// Reports / Notes / Quant citations open the right tab instead of
			// always falling back to Transcripts.
			dispatch(setAskKuralReferenceActiveTab(referenceDrawerTab));

			// Store which item should be auto-opened
			dispatch(setAutoOpenTranscriptItem(selectedRef));

			// Open drawer
			dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.expanded));

			// Trigger scroll restoration
			onCitationClick?.(citationId, studyType, references, messageId, scrollUniqueId);
		},
		[citationId, studyType, messageId, references, dispatch, onCitationClick, scrollUniqueId, displayInfo.hasData, referenceDrawerTab],
	);

	// Render nothing when terminal with no references (no error pill).
	if (!displayInfo.hasData && !shouldShowLoader && displayInfo?.count === 0) {
		return null;
	}

	// Render loading state
	if (shouldShowLoader) {
		return (
			<span className="inline-reference-tag-wrapper inline-reference-loading">
				<TagLoader className="spinning-loader" />
			</span>
		);
	}

	return (
		<Popover
			open={popoverOpen}
			trigger="hover"
			placement="bottomLeft"
			onOpenChange={(vis) => {
				// Only allow closing via mouseleave, not from internal click events
				if (!vis) {
					// Small delay to prevent closing during fast internal clicks
					setTimeout(() => {
						if (!document.querySelector(".transcript-preview-popover:hover, .reports-preview-popover:hover")) {
							setPopoverOpen(false);
						}
					}, 150);
				} else {
					setPopoverOpen(vis);
				}
			}}
			overlayClassName={`rounded-xl ${studyType === StudyType.Reports ? "reports-preview-popover" : "transcript-preview-popover"}`}
			overlayInnerStyle={{ padding: 0 }}
			mouseEnterDelay={0.3}
			mouseLeaveDelay={0.3}
			destroyOnHidden={true}
			getPopupContainer={() => document.body}
			content={
				// Reports has a dedicated preview component (page-image preview);
				// Transcripts / Notes / Quant share the generic transcript-style
				// popover, which renders from generic ref fields (file_name,
				// context_preview, entity_name) and works for all three.
				displayInfo.citationType === StudyType.Reports ? (
					<ReportsPreviewPopover references={references} onViewDetails={handleViewDetailsFromPopover} />
				) : (
					<TranscriptPreviewPopover references={references} onViewDetails={handleViewDetailsFromPopover} />
				)
			}>
			<span
				id={scrollUniqueId}
				className={`inline-reference-tag ${isSelected ? "selected" : ""}`}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				role="button"
				tabIndex={0}
				aria-label={`View ${displayInfo.type} (${displayInfo.count})`}>
				<>
					<span className="default-tag-icon mb-1">
						{renderTypeIcon()}
					</span>

					{displayInfo.citationType === StudyType.Reports && displayInfo?.count === 1 ? (
						<span>Report</span>
					) : displayInfo.citationType === StudyType.Reports && displayInfo?.count > 1 ? (
						<span>Reports ({displayInfo?.count})</span>
					) : displayInfo?.count > 1 ? (
						<span>({displayInfo?.count})</span>
					) : (
						<span></span>
					)}
				</>
				<i className="material-symbols-outlined text-base">chevron_right</i>
			</span>
		</Popover>
	);
};

export default InlineReferenceTag;
